import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Chip,
  Skeleton,
  Tabs,
  Tab,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import RefreshButton from '../components/RefreshButton';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import type {
  BackupDestination,
  CloudProvider,
  DestinationStatus,
} from '@/types/backup-destination.types';
import { backupDestinationService } from '@/services/backup-destination.service';
import OnPremiseConfigForm from '@/components/OnPremiseConfigForm';
import { formatRelativeTime } from '@/utils/relativeTime';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`backup-dest-tabpanel-${index}`}
      aria-labelledby={`backup-dest-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Cloud provider display config
const cloudProviderConfig: Record<CloudProvider, { name: string; color: string; shortName: string }> = {
  aws: { name: 'Amazon S3', color: '#FF9900', shortName: 'AWS' },
  azure: { name: 'Azure Blob Storage', color: '#0078D4', shortName: 'AZ' },
  gcp: { name: 'Google Cloud Storage', color: '#4285F4', shortName: 'GCP' },
};

// Status display config
const statusConfig: Record<DestinationStatus, { label: string; color: 'success' | 'error' | 'warning' | 'default'; icon: React.ReactNode }> = {
  healthy: { label: '已連線', color: 'success', icon: <CheckCircleIcon /> },
  unhealthy: { label: '連線異常', color: 'error', icon: <ErrorIcon /> },
  not_configured: { label: '未設定', color: 'default', icon: <WarningIcon /> },
  verifying: { label: '驗證中', color: 'warning', icon: <RefreshIcon /> },
};

// Read-only field component
interface ReadOnlyFieldProps {
  label: string;
  value: string;
  isSecret?: boolean;
}

// QA #79: both cloud and on-prem cards say how long ago the connection was
// last checked (cloud: the console's periodic check, QA #95, else the
// worker's own report; on-prem: the console's own test), with the exact time
// on hover.
function LastCheckedLine({ at }: { at?: string }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
      上次檢查連線：
      {at ? (
        <Tooltip title={new Date(at).toLocaleString('zh-TW')} arrow>
          <span>{formatRelativeTime(at)}</span>
        </Tooltip>
      ) : (
        '尚未檢查'
      )}
    </Typography>
  );
}

function ReadOnlyField({ label, value, isSecret = false }: ReadOnlyFieldProps) {
  const displayValue = isSecret ? '••••••••••••' : value || '-';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          bgcolor: 'grey.100',
          p: 1.5,
          borderRadius: 1,
          fontFamily: isSecret ? 'inherit' : 'monospace',
          fontSize: '0.875rem',
          color: value ? 'text.primary' : 'text.disabled',
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

export default function BackupDestinations() {
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState<BackupDestination[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Menu states
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDestination, setSelectedDestination] = useState<BackupDestination | null>(null);

  // Dialog states
  const [onPremiseFormOpen, setOnPremiseFormOpen] = useState(false);
  // QA #105: the form keeps its fields in its own state, initialised once from
  // initialData. A new key on every open remounts it, so 新增 starts empty and
  // 修改 shows the host being edited instead of whatever was typed last time.
  const [onPremiseFormSession, setOnPremiseFormSession] = useState(0);
  const [editingDestination, setEditingDestination] = useState<BackupDestination | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  const loadDestinations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await backupDestinationService.getDestinations();
      setDestinations(response.destinations || []);
    } catch (err) {
      console.error('Failed to load destinations:', err);
      setError('載入備份目的地失敗');
      setDestinations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDestinations();
  }, [loadDestinations]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, dest: BackupDestination) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedDestination(dest);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // ADR-041 D3: the test runs in the background; 202 puts the row in
  // `verifying` and we poll it until the outcome lands (healthy / unhealthy).
  const handleTestConnection = async (dest: BackupDestination) => {
    handleMenuClose();
    setTestingConnection(dest.id);
    try {
      const started = await backupDestinationService.testConnection(dest.id);
      setDestinations((prev) => prev.map((d) => (d.id === started.id ? started : d)));
      const final = await backupDestinationService.waitForConnectionTest(dest.id);
      setDestinations((prev) => prev.map((d) => (d.id === final.id ? final : d)));
      if (final.status === 'healthy') {
        setSuccessMessage(`連線測試成功：${final.display_name}`);
      } else {
        setError(`連線測試失敗: ${final.last_error || '未知錯誤'}`);
      }
    } catch (err) {
      const apiErr = err as { code?: string; message?: string };
      if (apiErr?.code === 'TEST_IN_PROGRESS') {
        setError('此目的地的連線測試仍在進行中，請稍候再試');
      } else if (apiErr?.code === 'CLOUD_READ_ONLY') {
        setError('雲端儲存由 worker 環境設定管理，無法從主控台測試');
      } else {
        setError(`連線測試失敗: ${apiErr?.message || '未知錯誤'}`);
      }
      loadDestinations();
    } finally {
      setTestingConnection(null);
    }
  };

  const handleEnableDisable = async (dest: BackupDestination) => {
    handleMenuClose();
    try {
      if (dest.is_enabled) {
        await backupDestinationService.disableDestination(dest.id);
        setSuccessMessage(`已停用 ${dest.display_name}`);
      } else {
        await backupDestinationService.enableDestination(dest.id);
        setSuccessMessage(`已啟用 ${dest.display_name}`);
      }
      loadDestinations();
    } catch {
      setError(dest.is_enabled ? '停用失敗' : '啟用失敗');
    }
  };

  const handleDeleteClick = (dest: BackupDestination) => {
    handleMenuClose();
    setSelectedDestination(dest);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDestination) return;
    try {
      await backupDestinationService.deleteDestination(selectedDestination.id);
      setSuccessMessage(`已刪除 ${selectedDestination.display_name}`);
      setDeleteDialogOpen(false);
      setSelectedDestination(null);
      loadDestinations();
    } catch {
      setError('刪除失敗');
    }
  };

  const handleEditClick = (dest: BackupDestination) => {
    handleMenuClose();
    if (dest.destination_type === 'on_premise') {
      setEditingDestination(dest);
      setOnPremiseFormSession((n) => n + 1);
      setOnPremiseFormOpen(true);
    }
  };

  const handleOnPremiseSubmit = async (data: Parameters<typeof backupDestinationService.createOnPremiseDestination>[0]) => {
    if (editingDestination) {
      await backupDestinationService.updateDestination(editingDestination.id, data);
      setSuccessMessage('地端主機更新成功');
    } else {
      await backupDestinationService.createOnPremiseDestination(data);
      setSuccessMessage('地端主機新增成功');
    }
    setOnPremiseFormOpen(false);
    setEditingDestination(null);
    loadDestinations();
  };

  const handleOnPremiseFormClose = () => {
    setOnPremiseFormOpen(false);
    setEditingDestination(null);
  };

  const renderStatusChip = (status: DestinationStatus) => {
    const config = statusConfig[status];
    return (
      <Chip
        icon={config.icon as React.ReactElement}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const renderSkeleton = () => (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
  );

  const renderCloudCard = (dest: BackupDestination) => {
    if (!dest.cloud_provider) return null;
    const config = cloudProviderConfig[dest.cloud_provider];

    return (
      <Grid item xs={12} md={4} key={dest.id}>
        <Paper
          sx={{
            p: 3,
            borderTop: `4px solid ${config.color}`,
            height: '100%',
            position: 'relative',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: config.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              >
                {config.shortName}
              </Box>
              <Box>
                <Typography variant="h6">{config.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dest.display_name}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {renderStatusChip(dest.status)}
              {/* ADR-041: cloud destinations are read-only projections of the
                  worker's environment — no edit / test / delete menu here. */}
              <Tooltip title="由 worker 環境設定（Key Vault）管理，主控台唯讀">
                <Chip label="唯讀" size="small" variant="outlined" />
              </Tooltip>
            </Stack>
          </Stack>

          {/* QA #79: how long ago the connection was last checked; QA #95: the
              console's periodic check, so it moves every check interval */}
          <LastCheckedLine at={dest.last_verified_at} />

          {dest.status === 'unhealthy' && dest.last_error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dest.last_error}
            </Alert>
          )}

          {dest.cloud_provider === 'aws' && (
            <>
              <ReadOnlyField label="Bucket Name" value={dest.aws_bucket_name || dest.bucket_name || '-'} />
              <ReadOnlyField label="Region" value={dest.aws_region || dest.region || '-'} />
              <ReadOnlyField label="Access Key ID" value={dest.aws_access_key_id || dest.access_key_id || '-'} />
            </>
          )}

          {dest.cloud_provider === 'azure' && (
            <>
              <ReadOnlyField label="Storage Account" value={dest.azure_storage_account || dest.storage_account || '-'} />
              <ReadOnlyField label="Container Name" value={dest.azure_container_name || dest.container_name || '-'} />
            </>
          )}

          {dest.cloud_provider === 'gcp' && (
            <>
              <ReadOnlyField label="Bucket Name" value={dest.gcp_bucket_name || dest.bucket_name || '-'} />
              <ReadOnlyField label="Project ID" value={dest.gcp_project_id || dest.project_id || '-'} />
            </>
          )}

          {testingConnection === dest.id && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderOnPremiseCard = (dest: BackupDestination) => {
    return (
      <Grid item xs={12} md={6} lg={4} key={dest.id}>
        <Paper
          sx={{
            p: 3,
            borderTop: '4px solid #6b7280',
            height: '100%',
            position: 'relative',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mb={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <StorageIcon />
              </Box>
              <Box>
                <Typography variant="h6">{dest.display_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dest.protocol?.toUpperCase()} - {dest.host}:{dest.port}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {dest.is_enabled && (
                <Tooltip title="已啟用">
                  <Chip label="啟用" color="primary" size="small" />
                </Tooltip>
              )}
              {renderStatusChip(dest.status)}
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, dest)}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Stack>

          <ReadOnlyField label="主機" value={`${dest.host}:${dest.port}`} />
          <ReadOnlyField label="備份路徑" value={dest.base_path || ''} />
          <ReadOnlyField label="認證方式" value={dest.auth_type === 'key' ? 'SSH 金鑰' : '密碼認證'} />
          <ReadOnlyField label="使用者" value={dest.username || ''} />

          {/* QA #79: how long ago the connection was last checked */}
          <LastCheckedLine at={dest.last_verified_at} />

          {dest.last_error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dest.last_error}
            </Alert>
          )}

          {testingConnection === dest.id && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  const cloudDestinations = destinations.filter(d => d.destination_type === 'cloud');
  const onPremiseDestinations = destinations.filter(d => d.destination_type === 'on_premise');

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            備份目的地設定
          </Typography>
          <Typography variant="body2" color="text.secondary">
            設定雲端儲存空間及地端主機備份位置。新增、啟用、停用或修改地端主機後約 1 分鐘內生效。系統每 30
            分鐘自動檢查各目的地的連線與寫入權限，失敗會自動重試 3 次，仍失敗才標示「連線異常」並依告警設定寄出「備份目的地連線失敗」通知。
          </Typography>
        </Box>
        <RefreshButton onClick={loadDestinations} loading={loading} />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<CloudIcon />}
            iconPosition="start"
            label={`雲端儲存 (${cloudDestinations.length})`}
          />
          <Tab
            icon={<StorageIcon />}
            iconPosition="start"
            label={`地端主機 (${onPremiseDestinations.length})`}
          />
        </Tabs>
      </Box>

      {/* Cloud Storage Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper sx={{ p: 3 }}>{renderSkeleton()}</Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {cloudDestinations.map(renderCloudCard)}
            {cloudDestinations.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <CloudIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">
                    worker 尚未設定任何雲端儲存。雲端供應商與憑證由 infra 透過 worker 環境（Key Vault）管理，主控台僅顯示。
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>

      {/* On-Premise Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingDestination(null);
              setOnPremiseFormSession((n) => n + 1);
              setOnPremiseFormOpen(true);
            }}
          >
            新增地端主機
          </Button>
        </Box>

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Paper sx={{ p: 3 }}>{renderSkeleton()}</Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {onPremiseDestinations.map(renderOnPremiseCard)}
            {onPremiseDestinations.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <StorageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>
                    尚未設定任何地端主機備份目的地
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    點擊上方「新增地端主機」按鈕來設定 SFTP 或 SMB 備份位置
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedDestination && handleTestConnection(selectedDestination)}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>測試連線</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedDestination && handleEnableDisable(selectedDestination)}>
          <ListItemIcon>
            {selectedDestination?.is_enabled ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{selectedDestination?.is_enabled ? '停用' : '啟用'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedDestination && handleEditClick(selectedDestination)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>編輯</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => selectedDestination && handleDeleteClick(selectedDestination)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>刪除</ListItemText>
        </MenuItem>
      </Menu>

      {/* On-Premise Form Dialog */}
      <OnPremiseConfigForm
        key={`onprem-form-${onPremiseFormSession}`}
        open={onPremiseFormOpen}
        onClose={handleOnPremiseFormClose}
        onSubmit={handleOnPremiseSubmit}
        initialData={editingDestination ? {
          display_name: editingDestination.display_name,
          protocol: editingDestination.protocol as 'sftp' | 'smb',
          host: editingDestination.host || '',
          port: editingDestination.port || 22,
          base_path: editingDestination.base_path || '',
          auth_type: editingDestination.auth_type as 'password' | 'key',
          username: editingDestination.username || '',
          domain: editingDestination.domain,
          use_tls: editingDestination.use_tls,
          verify_host: editingDestination.verify_host,
          connection_timeout: editingDestination.connection_timeout,
          transfer_timeout: editingDestination.transfer_timeout,
          retry_attempts: editingDestination.retry_attempts,
        } : undefined}
        isEdit={!!editingDestination}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要刪除「{selectedDestination?.display_name}」嗎？此操作無法復原。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
